// ============================================================
// PortalKit — small SSR-safe presentational primitives shared by the
// Olivium Project Portal Factory surfaces. Inline styles reference the V2
// ink tokens; no CSS-in-JS lib. Every state renderer is honest — it never
// invents data and always labels backend-not-ready / auth-required /
// local-only states plainly.
// ============================================================

import type { CSSProperties, ReactNode } from 'react'
import { CC_INK, CC_MONO } from '../../shell'
import {
  READINESS_META, type ReadinessStatus, type ModuleStatus, OLIVIUM_ACCENT,
} from './portalFactory'

export const PORTAL_HONESTY = {
  label: 'PORTAL FACTORY · NOT FAKED',
  title: 'Portals are real saved records (sq_project_portals — migration 20260709000003 applied 2026-07-10). Nothing here fabricates a portal, module, action, note, blocker, or receipt. External client access and realtime are deferred.',
}

const STATUS_META: Record<ModuleStatus, { label: string; accent: string }> = {
  POWERED: { label: 'POWERED', accent: '#059669' },
  SOURCE_READY: { label: 'SOURCE READY', accent: '#2563eb' },
  BACKEND_DEFERRED: { label: 'BACKEND DEFERRED', accent: '#b8862a' },
  INTEGRATION_DEFERRED: { label: 'INTEGRATION DEFERRED', accent: '#b8862a' },
  REALTIME_DEFERRED: { label: 'REALTIME DEFERRED', accent: '#b8862a' },
  TEMPLATE: { label: 'TEMPLATE', accent: '#8a94a0' },
}

export function moduleStatusMeta(status: ModuleStatus) {
  return STATUS_META[status] ?? STATUS_META.TEMPLATE
}

const pillBase: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 9, fontWeight: 800,
  letterSpacing: '0.1em', fontFamily: CC_MONO, padding: '3px 8px', borderRadius: 5, whiteSpace: 'nowrap',
}

export function ModuleStatusBadge({ status }: { status: ModuleStatus }) {
  const m = moduleStatusMeta(status)
  return <span style={{ ...pillBase, color: m.accent, background: m.accent + '18', border: `1px solid ${m.accent}33` }}>{m.label}</span>
}

export function ReadinessPill({ status }: { status: ReadinessStatus }) {
  const m = READINESS_META[status] ?? READINESS_META.planning
  return <span style={{ ...pillBase, color: m.accent, background: m.accent + '18', border: `1px solid ${m.accent}33` }}>{m.label.toUpperCase()}</span>
}

export function SectionCard({ title, hint, right, children, accent }: { title: string; hint?: string; right?: ReactNode; children: ReactNode; accent?: string }) {
  return (
    <section style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, padding: 16, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: hint ? 4 : 12 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: accent || OLIVIUM_ACCENT, flexShrink: 0 }} />
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 800, color: CC_INK.primary, letterSpacing: '0.01em', flex: 1 }}>{title}</h3>
        {right}
      </div>
      {hint && <p style={{ margin: '0 0 12px 17px', fontSize: 11, color: CC_INK.muted }}>{hint}</p>}
      {children}
    </section>
  )
}

/** An honest state block for loading / auth-required / error / empty. Never
 *  substitutes fabricated data for a failure. */
export function StateBlock({ kind, title, body }: { kind: 'loading' | 'auth' | 'error' | 'empty'; title: string; body?: string }) {
  const accent = kind === 'error' ? '#dc2626' : kind === 'auth' ? '#b8862a' : CC_INK.muted
  return (
    <div style={{ padding: '20px 16px', border: `1px dashed ${accent}44`, borderRadius: 10, background: accent + '0d', textAlign: 'center' }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: accent, letterSpacing: '0.04em' }}>{title}</div>
      {body && <div style={{ marginTop: 6, fontSize: 11, color: CC_INK.muted, lineHeight: 1.5 }}>{body}</div>}
      {kind === 'auth' && <div style={{ marginTop: 6, fontSize: 10, color: CC_INK.faint, fontFamily: CC_MONO }}>Nothing is fabricated — no fabricated data is shown.</div>}
    </div>
  )
}

/** The standard backend-not-migrated banner shown when the error is a
 *  missing table. Callers pass the humanized error string. */
export function BackendNotReadyBanner({ message }: { message: string }) {
  return (
    <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', color: '#d1a34a', fontSize: 11, fontWeight: 700, marginBottom: 14 }}>
      Backend not ready — {message}
    </div>
  )
}

export const chipBtn: CSSProperties = {
  padding: '6px 12px', borderRadius: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.04)',
  border: '1px solid rgba(255,255,255,0.1)', color: CC_INK.secondary, fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
}

export const primaryBtn: CSSProperties = {
  padding: '8px 16px', borderRadius: 9, cursor: 'pointer', background: OLIVIUM_ACCENT + '22',
  border: `1px solid ${OLIVIUM_ACCENT}55`, color: '#b794f6', fontSize: 12, fontWeight: 800, fontFamily: 'inherit',
}

export const inputStyle: CSSProperties = {
  width: '100%', padding: '9px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.1)', color: CC_INK.primary, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box',
}

export const fieldLabel: CSSProperties = {
  display: 'block', fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', color: CC_INK.muted, fontFamily: CC_MONO, marginBottom: 5,
}

export function TemplateBadge({ label }: { label: string }) {
  return <span style={{ ...pillBase, color: CC_INK.muted, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>{label}</span>
}
